import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { EnrichedServiceReport, SrTab } from "../../store/serviceReportsSlice";
import serviceReportsApi from "../../api/serviceReportsApi";
import { ServiceReportFile } from "../../../machinery/models/ServiceReportsModel";
import FormFileUploader from "../../../../shared/components/redesign/forms/FormFileUploader";
import type { FileItem } from "../../../../shared/components/redesign/forms/FileRow";
import FormChipSelector from "../../../../shared/components/redesign/forms/FormChipSelector";
import type { ChipOption } from "../../../../shared/components/redesign/forms/FormChipSelector";

// ─── Constants ─────────────────────────────────────────────
const PANEL_WIDTH = 480;
const FOCUS_COLOR = "#D4572B";
const TEXT_PRIMARY = "#0C1117";
const TEXT_BODY = "#4A4F57";
const SECTION_LABEL_COLOR = "#8C9099";
const INPUT_BG = "#F7F5F0";
const BORDER_COLOR = "#E8E5DF";
const FONT_SORA = "'Sora', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

// ─── Helpers ────────────────────────────────────────────────

function formatSrNumber(num: string | undefined): string {
  if (!num) return "SR-???";
  if (num.startsWith("SR-")) return num;
  const short = num.length > 4 ? num.slice(-4) : num;
  return `SR-${short}`;
}

// ─── Urgency chip config ───────────────────────────────────

const buildUrgencyChips = (translate: (key: string) => string): ChipOption<number>[] => [
  { code: 1, color: "#DC2626", label: translate("serviceReports.urgency.critical"), dotOnly: true },
  { code: 2, color: "#E8A817", label: translate("serviceReports.urgency.high"), dotOnly: true },
  { code: 4, color: "#3B82F6", label: translate("serviceReports.urgency.normal"), dotOnly: true },
  { code: 0, color: "#8C9099", label: translate("serviceReports.urgency.info"), dotOnly: true },
];

// ─── Tab label map ─────────────────────────────────────────

const TAB_LABELS: Record<SrTab, string> = {
  untriaged: "Untriaged",
  internal: "Internal",
  external: "External",
  resolved: "Resolved",
};

// ─── Tab badge colors ──────────────────────────────────────

const TAB_BADGE_COLORS: Record<SrTab, { bg: string; text: string }> = {
  untriaged: { bg: "#F4F6F9", text: "#6B7280" },
  internal: { bg: "#EBF5FB", text: "#2980B9" },
  external: { bg: "#FEF9E7", text: "#E8A817" },
  resolved: { bg: "#EAFAF1", text: "#27AE60" },
};

// ─── Urgency badge colors ──────────────────────────────────

const URGENCY_BADGE_MAP: Record<number, { bg: string; label: string }> = {
  1: { bg: "#DC2626", label: "Crit" },
  2: { bg: "#E8A817", label: "High" },
  4: { bg: "#3B82F6", label: "Norm" },
  0: { bg: "#8C9099", label: "Info" },
};

// ─── Props ──────────────────────────────────────────────────

interface EditSrPanelProps {
  open: boolean;
  item: EnrichedServiceReport;
  onClose: () => void;
  onUpdated: (srNumber: string) => void;
  translate: (key: string) => string;
}

// ─── Component ──────────────────────────────────────────────

const EditSrPanel: React.FC<EditSrPanelProps> = ({
  open,
  item,
  onClose,
  onUpdated,
  translate,
}) => {
  const { sr, tab } = item;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [urgency, setUrgency] = useState<number>(4);
  const [alarmCode, setAlarmCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // File uploader state
  const [fileItems, setFileItems] = useState<FileItem[]>([]);
  const [existingFiles, setExistingFiles] = useState<ServiceReportFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);

  const urgencyChips = useMemo(() => buildUrgencyChips(translate), [translate]);

  // Pre-populate from SR data when panel opens
  useEffect(() => {
    if (open) {
      setTitle(sr.title || "");
      setDescription(sr.description || "");
      setNotes(sr.notes || "");
      setUrgency(sr.urgency ?? 4);
      setAlarmCode(sr.alarmCode || "");
      setError("");
      setSubmitting(false);
      setFileItems([]);
      setExistingFiles([]);
    }
  }, [open, sr]);

  // Load file details when panel opens (lazy-load from per-machine endpoint)
  useEffect(() => {
    if (!open || !sr.machineId || !sr.number) return;

    const srFiles = sr.files?.filter((f) => Boolean(f.content)) ?? [];
    if (srFiles.length > 0) {
      setExistingFiles(srFiles);
      setFileItems(mapSrFilesToFileItems(srFiles));
      return;
    }

    // Fetch from per-machine endpoint for full file content
    const fileCount = (sr as any).fileCount ?? sr.files?.length ?? 0;
    if (fileCount === 0) return;

    setFilesLoading(true);
    serviceReportsApi
      .getServiceReportDetail(sr.machineId, sr.number)
      .then((detail) => {
        if (detail?.files && detail.files.length > 0) {
          setExistingFiles(detail.files);
          setFileItems(mapSrFilesToFileItems(detail.files));
        }
      })
      .catch(() => { /* files unavailable — show empty */ })
      .finally(() => setFilesLoading(false));
  }, [open, sr]);

  // Inject keyframes
  useEffect(() => {
    const id = "edit-sr-panel-keyframes";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes editSrSlideIn {
          0% { transform: translateX(100%); }
          100% { transform: translateX(0); }
        }
        @keyframes editSrBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Esc key handler
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  // Lock body scroll while panel is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const canSubmit = title.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !sr.number) return;
    setSubmitting(true);
    setError("");

    try {
      await serviceReportsApi.updateSr(sr.number, {
        title: title.trim(),
        description: description.trim(),
        notes: notes.trim() || undefined,
        urgency,
        alarmCode: alarmCode.trim() || undefined,
      });
      // NOTE: File upload via the Edit SR endpoint is not yet supported by the
      // Operations serviceReportsApi. New files collected by FormFileUploader
      // are tracked in state but not uploaded on save. Backend wiring needed.
      onUpdated(sr.number);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to update");
      setSubmitting(false);
    }
  };

  const handleFilesChange = useCallback((newFiles: FileItem[]) => {
    setFileItems(newFiles);
  }, []);

  const currentUrgencyBadge = URGENCY_BADGE_MAP[urgency] || URGENCY_BADGE_MAP[4];
  const tabBadgeColor = TAB_BADGE_COLORS[tab];

  if (!open) return null;

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 1400, display: "flex" }}>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(12,17,23,0.18)",
          animation: "editSrBackdropIn 0.2s ease",
        }}
      />

      {/* Panel — slides in from right */}
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: `${PANEL_WIDTH}px`,
          maxWidth: "100vw",
          backgroundColor: "#FFFFFF",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
          animation: "editSrSlideIn 0.3s ease",
        }}
      >
        {/* ─── Header — white bg, title + X close ────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${BORDER_COLOR}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <Box
              sx={{
                fontFamily: FONT_SORA,
                fontSize: "18px",
                fontWeight: 700,
                color: TEXT_PRIMARY,
                mb: "10px",
              }}
            >
              {translate("serviceReports.actions.editTitle")}
            </Box>

            {/* Context row: SR number + tab badge + urgency badge */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <Box
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#3B82F6",
                }}
              >
                {formatSrNumber(sr.number)}
              </Box>
              <Box
                sx={{
                  fontFamily: FONT_SORA,
                  fontSize: "10px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  padding: "2px 8px",
                  borderRadius: "4px",
                  backgroundColor: tabBadgeColor.bg,
                  color: tabBadgeColor.text,
                }}
              >
                {TAB_LABELS[tab]}
              </Box>
              <Box
                sx={{
                  fontFamily: FONT_SORA,
                  fontSize: "10px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  padding: "2px 8px",
                  borderRadius: "4px",
                  backgroundColor: currentUrgencyBadge.bg,
                  color: "#FFFFFF",
                }}
              >
                {currentUrgencyBadge.label}
              </Box>
            </Box>

            {/* Machine context line */}
            {(sr.machineModel || (sr as any).manufacturerName || (sr as any).locationName) && (
              <Box
                sx={{
                  fontFamily: FONT_SORA,
                  fontSize: "13px",
                  color: SECTION_LABEL_COLOR,
                  mt: "6px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {[
                  sr.machineModel,
                  (sr as any).machineInternalNumber ? `SN ${(sr as any).machineInternalNumber}` : null,
                  (sr as any).locationName,
                ]
                  .filter(Boolean)
                  .join(" \u00B7 ")}
              </Box>
            )}
          </Box>

          {/* X close button */}
          <Box
            onClick={onClose}
            sx={{
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#F2F0EB",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              flexShrink: 0,
              mt: "2px",
              "&:hover": { backgroundColor: "#E8E5DF" },
            }}
          >
            <CloseRounded sx={{ fontSize: 18, color: TEXT_BODY }} />
          </Box>
        </Box>

        {/* ─── Scrollable Body ───────────────────────────── */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            scrollbarWidth: "thin",
            scrollbarColor: "#C1C7CD transparent",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#C1C7CD", borderRadius: "3px" },
          }}
        >
          {/* TITLE */}
          <Box sx={{ mb: "16px" }}>
            <SectionLabel>{translate("serviceReports.actions.fieldTitle")?.toUpperCase() || "TITLE"}</SectionLabel>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={inputStyle}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
          </Box>

          {/* URGENCY */}
          <Box sx={{ mb: "16px" }}>
            <SectionLabel>{translate("serviceReports.actions.fieldUrgency")?.toUpperCase() || "URGENCY"}</SectionLabel>
            <Box
              sx={{
                /* Keep dot-only chip text dark when selected */
                "& > div > div": {
                  "& > div:last-child": { color: `${TEXT_PRIMARY} !important` },
                },
              }}
            >
              <FormChipSelector
                options={urgencyChips}
                value={urgency}
                onChange={setUrgency}
              />
            </Box>
          </Box>

          {/* DESCRIPTION */}
          <Box sx={{ mb: "16px" }}>
            <SectionLabel>{translate("serviceReports.actions.fieldDescription")?.toUpperCase() || "DESCRIPTION"}</SectionLabel>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              style={{ ...inputStyle, resize: "vertical", lineHeight: "1.6" }}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
          </Box>

          {/* TECHNICAL NOTES */}
          <Box sx={{ mb: "16px" }}>
            <SectionLabel>{translate("serviceReports.actions.fieldNotes")?.toUpperCase() || "TECHNICAL NOTES"}</SectionLabel>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add technical notes..."
              style={{ ...inputStyle, resize: "vertical", lineHeight: "1.6" }}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
          </Box>

          {/* ALARM CODE */}
          <Box sx={{ mb: "16px" }}>
            <SectionLabel>
              {translate("serviceReports.actions.fieldAlarmCode")?.toUpperCase() || "ALARM CODE"}
              <Box
                component="span"
                sx={{
                  fontWeight: 400,
                  color: SECTION_LABEL_COLOR,
                  ml: "6px",
                  textTransform: "none",
                  letterSpacing: "normal",
                  fontSize: "11px",
                }}
              >
                ({translate("serviceReports.actions.optional") || "optional"})
              </Box>
            </SectionLabel>
            <input
              type="text"
              value={alarmCode}
              onChange={(e) => setAlarmCode(e.target.value)}
              placeholder="ALM-0000"
              style={{
                ...inputStyle,
                fontFamily: FONT_MONO,
                letterSpacing: "0.05em",
              }}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
          </Box>

          {/* Divider before attachments */}
          <Box sx={{ height: "1px", backgroundColor: BORDER_COLOR, mb: "16px" }} />

          {/* ATTACHMENTS */}
          <Box sx={{ mb: "8px" }}>
            <SectionLabel>
              {translate("serviceReports.expanded.attachments")?.toUpperCase() || "ATTACHMENTS"}
            </SectionLabel>
            {filesLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontFamily: FONT_SORA,
                  fontSize: "12px",
                  color: SECTION_LABEL_COLOR,
                  py: "12px",
                }}
              >
                <Box
                  sx={{
                    width: "14px",
                    height: "14px",
                    border: "2px solid #E8E5DF",
                    borderTopColor: FOCUS_COLOR,
                    borderRadius: "50%",
                    animation: "spin 0.6s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
                Loading attachments...
              </Box>
            ) : (
              <FormFileUploader
                files={fileItems}
                onFilesChange={handleFilesChange}
                variant="image-preview"
                accept="image/*,.pdf,.doc,.docx"
                maxSize={10 * 1024 * 1024}
                labels={{
                  dragAndDrop: "Drag & drop files here",
                  browse: "Browse files",
                  or: "or",
                  formatHint: "PNG, JPG, PDF, DOC up to 10MB",
                  addMore: "+ Add more files",
                }}
              />
            )}
          </Box>
        </Box>

        {/* Error */}
        {error && (
          <Box
            sx={{
              fontFamily: FONT_SORA,
              fontSize: "12px",
              color: "#DC2626",
              px: "24px",
              pb: "8px",
            }}
          >
            {error}
          </Box>
        )}

        {/* ─── Sticky Footer ─────────────────────────────── */}
        <Box
          sx={{
            borderTop: `1px solid ${BORDER_COLOR}`,
            padding: "16px 24px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "12px",
            flexShrink: 0,
          }}
        >
          <Box
            onClick={onClose}
            sx={{
              fontFamily: FONT_SORA,
              fontSize: "13px",
              fontWeight: 500,
              color: SECTION_LABEL_COLOR,
              cursor: "pointer",
              padding: "10px 16px",
              borderRadius: "8px",
              transition: "all 0.15s ease",
              "&:hover": { backgroundColor: "#F2F0EB", color: TEXT_BODY },
            }}
          >
            {translate("serviceReports.actions.cancel")}
          </Box>
          <Box
            onClick={handleSubmit}
            sx={{
              fontFamily: FONT_SORA,
              fontSize: "13px",
              fontWeight: 600,
              color: "#FFFFFF",
              backgroundColor: canSubmit ? FOCUS_COLOR : "#C1BDB7",
              padding: "10px 28px",
              borderRadius: "8px",
              cursor: canSubmit ? "pointer" : "default",
              transition: "all 0.15s ease",
              "&:hover": canSubmit
                ? {
                    backgroundColor: "#BF4D26",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(212,87,43,0.25)",
                  }
                : {},
            }}
          >
            {submitting
              ? translate("serviceReports.actions.saving")
              : translate("serviceReports.actions.saveChanges")}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Section Label sub-component ────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      fontFamily: "'Sora', sans-serif",
      fontSize: "11px",
      fontWeight: 600,
      color: "#8C9099",
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      mb: "8px",
    }}
  >
    {children}
  </Box>
);

// ─── Shared styles ──────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  fontFamily: "'Sora', sans-serif",
  fontSize: "13px",
  color: "#0C1117",
  backgroundColor: "#F7F5F0",
  border: "1.5px solid transparent",
  borderRadius: "8px",
  padding: "10px 14px",
  outline: "none",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
};

const focusHandler = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  e.target.style.borderColor = "#D4572B";
  e.target.style.boxShadow = "0 0 0 3px rgba(212,87,43,0.10)";
};

const blurHandler = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  e.target.style.borderColor = "transparent";
  e.target.style.boxShadow = "none";
};

// ─── File mapping helper ────────────────────────────────────

/** Convert ServiceReportFile[] to FileItem[] for FormFileUploader. */
function mapSrFilesToFileItems(files: ServiceReportFile[]): FileItem[] {
  return files.map((f, i) => ({
    id: `existing-${i}`,
    name: f.name,
    size: f.size || 0,
    type: guessMimeType(f.name),
    content: f.content || undefined,
    url: f.content || undefined,
  }));
}

/** Guess MIME type from file name extension. */
function guessMimeType(name: string): string {
  const ext = name.toLowerCase().split(".").pop() || "";
  const mimeMap: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    svg: "image/svg+xml",
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  };
  return mimeMap[ext] || "application/octet-stream";
}

export default EditSrPanel;
